import { useQuery } from '@tanstack/react-query';

import { fetchLiqMap, type LiqMapSnapshot } from '@/lib/api/speed';
import { usePageVisible } from '@/lib/hooks/usePageVisible';
import { queryKeys } from '@/lib/query';

/**
 * The server recomputes the map every few minutes; polling faster than that
 * only re-reads the same document. 60s keeps the "updated N min ago" honest
 * without hammering a public route.
 */
const REFETCH_MS = 60_000;

export interface LiqMapState {
    snapshot: LiqMapSnapshot | null;
    coin: string | null;
    isLoading: boolean;
    isError: boolean;
}

/**
 * The liquidation map for the charted coin.
 *
 * Public and unauthenticated, so it runs in demo mode too — a beginner on play
 * money is exactly who should be learning what the bands mean. Pauses while
 * the page is hidden, like the tape.
 */
export function useLiqMap(coin: string | null, enabled = true): LiqMapState {
    const visible = usePageVisible();
    const active = enabled && visible && !!coin;

    const query = useQuery({
        queryKey: queryKeys.speed.liqMap(coin ?? ''),
        queryFn: async ({ signal }) => fetchLiqMap(coin!, signal),
        enabled: active,
        staleTime: 30_000,
        refetchInterval: active ? REFETCH_MS : false,
        retry: 1,
    });

    return {
        snapshot: query.data?.snapshot ?? null,
        coin: query.data?.coin ?? null,
        isLoading: query.isLoading,
        isError: query.isError,
    };
}
